import type { PostMessageRequest, PostMessageResponse } from '@/types/postMessage';
import type { Page } from '@/types/page';

export interface PreviewUpdateData {
  page: Page;
  sections: any[];
  viewport: 'desktop' | 'tablet' | 'mobile';
  showDisabled: boolean;
  tenantConfig?: any;
}

class PostMessageService {
  private pendingRequests = new Map<string, {
    resolve: (data: any) => void;
    reject: (error: Error) => void;
    timeout: NodeJS.Timeout;
  }>();

  private requestIdCounter = 0;
  private readonly TIMEOUT_DURATION = 10000; // 10 seconds
  private previewUpdateCallbacks: ((data: PreviewUpdateData) => void)[] = [];
  private viewportUpdateCallbacks: ((viewport: string) => void)[] = [];
  private isInitialized = false;

  constructor() {
    this.setupMessageListener();
    this.startInitialization();
  }

  /**
   * Start initialization process
   */
  private startInitialization(): void {
    if (!this.isInIframe()) {
      console.log('PostMessageService: Not in iframe, marking as initialized');
      this.isInitialized = true;
      return;
    }

    console.log('PostMessageService: In iframe, sending ready signal...');
    
    // Send ready signal immediately
    setTimeout(() => this.sendReadySignal(), 500);
    
    // Send it periodically until we get a response
    const readyInterval = setInterval(() => {
      if (!this.isInitialized) {
        console.log('PostMessageService: 🔄 Re-sending ready signal...');
        this.sendReadySignal();
      } else {
        clearInterval(readyInterval);
      }
    }, 2000);

    // Stop trying after 30 seconds
    setTimeout(() => {
      clearInterval(readyInterval);
      if (!this.isInitialized) {
        console.log('PostMessageService: ❌ Timeout waiting for parent response');
      }
    }, 30000);
  }

  /**
   * Send ready signal to parent
   */
  private sendReadySignal(): void {
    if (!this.isInIframe()) return;

    try {
      console.log('PostMessageService: 📤 Sending IFRAME_READY to parent');
      this.sendMessageToParent({
        type: 'IFRAME_READY',
        timestamp: Date.now(),
        url: window.location.href
      });
    } catch (error) {
      console.error('PostMessageService: Failed to send ready signal:', error);
    }
  }

  /**
   * Setup listener for postMessage responses and updates
   */
  private setupMessageListener(): void {
    window.addEventListener('message', this.handleMessage.bind(this));
    console.log('PostMessageService: 👂 Message listener setup complete');
  }

  /**
   * Handle incoming postMessage responses and updates
   */
  private handleMessage(event: MessageEvent): void {
    // For iframe context, we expect messages from parent
    if (!this.isInIframe()) {
      return;
    }

    const message = event.data;
    
    if (!message || typeof message !== 'object') {
      return;
    }

    console.log('PostMessageService: 📨 Received message from parent:', message);

    // Handle different message types
    switch (message.type) {
      case 'PREVIEW_UPDATE':
        this.handlePreviewUpdate(message.data);
        break;
        
      case 'VIEWPORT_UPDATE':
        this.handleViewportUpdate(message.viewport);
        break;
        
      case 'PAGE_DATA':
      case 'PAGES_DATA':
      case 'HEALTH_CHECK':
      case 'ERROR':
        this.handleRequestResponse(message);
        break;
        
      default:
        console.log('PostMessageService: ⚠️ Unknown message type:', message.type);
    }
  }

  /**
   * Handle preview data updates from CMS
   */
  private handlePreviewUpdate(data: PreviewUpdateData): void {
    console.log('PostMessageService: 📄 Received preview update:', data);
    
    if (!data || !data.page) {
      console.warn('PostMessageService: ⚠️ Invalid preview data received');
      return;
    }

    this.isInitialized = true; // Mark as initialized when we receive data

    this.previewUpdateCallbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('PostMessageService: ❌ Error in preview update callback:', error);
      }
    });
  }

  /**
   * Handle viewport updates from CMS
   */
  private handleViewportUpdate(viewport: string): void {
    console.log('PostMessageService: 📱 Received viewport update:', viewport);
    this.viewportUpdateCallbacks.forEach(callback => {
      try {
        callback(viewport);
      } catch (error) {
        console.error('PostMessageService: ❌ Error in viewport update callback:', error);
      }
    });
  }

  /**
   * Handle request responses
   */
  private handleRequestResponse(response: PostMessageResponse): void {
    if (!response.id) {
      console.warn('PostMessageService: ⚠️ Response without ID received:', response);
      return;
    }

    const pendingRequest = this.pendingRequests.get(response.id);
    if (!pendingRequest) {
      console.warn('PostMessageService: ⚠️ No pending request found for ID:', response.id);
      return;
    }

    // Clear timeout and remove from pending requests
    clearTimeout(pendingRequest.timeout);
    this.pendingRequests.delete(response.id);

    console.log('PostMessageService: ✅ Processing response for request:', response.id, response);

    this.isInitialized = true; // Mark as initialized when we get a response

    if (response.success) {
      pendingRequest.resolve(response.data);
    } else {
      pendingRequest.reject(new Error(response.error || 'Unknown error'));
    }
  }

  /**
   * Generate unique request ID
   */
  private generateRequestId(): string {
    return `site_request_${++this.requestIdCounter}_${Date.now()}`;
  }

  /**
   * Send message to parent window
   */
  private sendMessageToParent(message: any): void {
    if (!window.parent || window.parent === window) {
      throw new Error('Not in iframe context');
    }

    try {
      console.log('PostMessageService: 📤 Sending message to parent:', message);
      window.parent.postMessage(message, '*');
    } catch (error) {
      console.error('PostMessageService: ❌ Failed to send message to parent:', error);
      throw new Error('Failed to send postMessage');
    }
  }

  /**
   * Send request to parent and return promise
   */
  private sendRequest<T = any>(request: Omit<PostMessageRequest, 'id'>): Promise<T> {
    if (!this.isInIframe()) {
      return Promise.reject(new Error('Not in iframe context'));
    }

    return new Promise((resolve, reject) => {
      const requestId = this.generateRequestId();
      const fullRequest: PostMessageRequest = {
        ...request,
        id: requestId,
      };

      console.log('PostMessageService: 📤 Sending request:', fullRequest);

      // Setup timeout
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        console.error('PostMessageService: ⏰ Request timeout for:', requestId);
        reject(new Error(`Request timeout for ${request.type}`));
      }, this.TIMEOUT_DURATION);

      // Store pending request
      this.pendingRequests.set(requestId, {
        resolve,
        reject,
        timeout,
      });

      // Send message to parent
      try {
        this.sendMessageToParent(fullRequest);
      } catch (error) {
        clearTimeout(timeout);
        this.pendingRequests.delete(requestId);
        reject(error);
      }
    });
  }

  // ========== PUBLIC API ==========

  /**
   * Get page by slug via postMessage
   */
  async getPageBySlug(slug: string): Promise<Page> {
    console.log('PostMessageService: 🔍 Requesting page by slug:', slug);
    
    const response = await this.sendRequest({
      type: 'GET_PAGE',
      payload: { slug },
    });

    if (!response.page) {
      throw new Error('No page data in response');
    }

    return response.page;
  }

  /**
   * Get home page via postMessage
   */
  async getHomePage(): Promise<Page> {
    console.log('PostMessageService: 🏠 Requesting home page');
    
    const response = await this.sendRequest({
      type: 'GET_HOME_PAGE',
    });

    if (!response.page) {
      throw new Error('No page data in response');
    }

    return response.page;
  }

  /**
   * Get pages list via postMessage
   */
  async getPages(query?: {
    type?: string;
    status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
    search?: string;
  }): Promise<{ pages: Page[]; pagination: any }> {
    console.log('PostMessageService: 📋 Requesting pages list:', query);
    
    const response = await this.sendRequest({
      type: 'GET_PAGES',
      payload: { query },
    });

    return {
      pages: response.pages || [],
      pagination: response.pagination || {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      }
    };
  }

  /**
   * Send health check to parent
   */
  async healthCheck(): Promise<any> {
    console.log('PostMessageService: 🩺 Sending health check');
    
    return this.sendRequest({
      type: 'HEALTH_CHECK',
    });
  }

  /**
   * Register callback for preview updates
   */
  onPreviewUpdate(callback: (data: PreviewUpdateData) => void): void {
    console.log('PostMessageService: 📝 Registering preview update callback');
    this.previewUpdateCallbacks.push(callback);
  }

  /**
   * Register callback for viewport updates
   */
  onViewportUpdate(callback: (viewport: string) => void): void {
    console.log('PostMessageService: 📱 Registering viewport update callback');
    this.viewportUpdateCallbacks.push(callback);
  }

  /**
   * Remove callback for preview updates
   */
  offPreviewUpdate(callback: (data: PreviewUpdateData) => void): void {
    const index = this.previewUpdateCallbacks.indexOf(callback);
    if (index > -1) {
      this.previewUpdateCallbacks.splice(index, 1);
    }
  }

  /**
   * Remove callback for viewport updates
   */
  offViewportUpdate(callback: (viewport: string) => void): void {
    const index = this.viewportUpdateCallbacks.indexOf(callback);
    if (index > -1) {
      this.viewportUpdateCallbacks.splice(index, 1);
    }
  }

  /**
   * Check if we're in iframe context
   */
  isInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      // If we can't access window.top due to same-origin policy, we're likely in an iframe
      return true;
    }
  }

  /**
   * Check if service is initialized and ready
   */
  isReady(): boolean {
    return this.isInitialized && this.isInIframe();
  }

  /**
   * Force re-initialization
   */
  reinitialize(): void {
    console.log('PostMessageService: 🔄 Force re-initialization');
    this.isInitialized = false;
    this.sendReadySignal();
  }

  /**
   * Send error to parent
   */
  reportError(error: string): void {
    if (this.isInIframe()) {
      try {
        this.sendMessageToParent({
          type: 'IFRAME_ERROR',
          error,
          timestamp: Date.now()
        });
      } catch (e) {
        console.error('PostMessageService: ❌ Failed to report error to parent:', e);
      }
    }
  }

  /**
   * Cleanup pending requests and callbacks
   */
  cleanup(): void {
    console.log('PostMessageService: 🧹 Cleaning up');
    
    // Clear pending requests
    this.pendingRequests.forEach(({ timeout }) => {
      clearTimeout(timeout);
    });
    this.pendingRequests.clear();

    // Clear callbacks
    this.previewUpdateCallbacks = [];
    this.viewportUpdateCallbacks = [];
  }

  /**
   * Get debug information
   */
  getDebugInfo(): any {
    return {
      isInIframe: this.isInIframe(),
      isInitialized: this.isInitialized,
      pendingRequests: this.pendingRequests.size,
      previewCallbacks: this.previewUpdateCallbacks.length,
      viewportCallbacks: this.viewportUpdateCallbacks.length,
      userAgent: navigator.userAgent,
      url: window.location.href,
      parentOrigin: this.isInIframe() ? document.referrer : 'Not in iframe',
      readyState: document.readyState
    };
  }
}

export const postMessageService = new PostMessageService();